import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { Spinner } from './Spinner';
import { ExportIcon as DownloadIcon } from './icons/ExportIcon';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileBlob?: Blob;
  fileUrl?: string;
  fileName?: string;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ isOpen, onClose, fileBlob, fileUrl, fileName }) => {
  const [fileContentUrl, setFileContentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isExternalUrl = useMemo(() => fileUrl?.startsWith('http') || false, [fileUrl]);

  const effectiveFileName = useMemo(() => {
    // Pro externí soubory zobrazíme URL jako název, jak si uživatel přál.
    // Pro interní soubory použijeme předaný, již zformátovaný název.
    if (isExternalUrl) {
        return fileUrl || fileName || 'soubor';
    }
    return fileName || 'soubor';
  }, [isExternalUrl, fileUrl, fileName]);


  useEffect(() => {
    if (!isOpen) {
        // Při zavření zrušíme platnost blob URL, abychom předešli memory leakům
        if (fileContentUrl && fileContentUrl.startsWith('blob:')) {
            URL.revokeObjectURL(fileContentUrl);
        }
        setFileContentUrl(null);
        setError(null);
        setIsLoading(false);
        return;
    }

    let isMounted = true;
    const fetchAndSetUrl = async () => {
        setIsLoading(true);
        setError(null);
        
        // Případ 1: Máme lokální Blob (např. z náhledu nahrávaného souboru)
        if (fileBlob) {
            const localUrl = URL.createObjectURL(fileBlob);
            if (isMounted) setFileContentUrl(localUrl);
            setIsLoading(false);
            return;
        }

        // Případ 2: Máme URL
        if (fileUrl) {
            // Podpřípad 2a: Jde o externí URL, použijeme ji přímo
            if (isExternalUrl) {
                if (isMounted) setFileContentUrl(fileUrl);
                setIsLoading(false);
                return;
            }
            
            // Podpřípad 2b: Jde o interní URL, musíme ji načíst bezpečně
            try {
                const blob = await apiService.downloadFileBlob(fileUrl);
                const localUrl = URL.createObjectURL(blob);
                if (isMounted) setFileContentUrl(localUrl);
            } catch (e: any) {
                if (isMounted) setError('Soubor se nepodařilo načíst. Zkuste to prosím znovu.');
                console.error("Error fetching internal file for preview:", e);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        } else {
            // Případ 3: Nemáme co zobrazit
            setIsLoading(false);
        }
    };

    fetchAndSetUrl();

    return () => {
        isMounted = false;
        if (fileContentUrl && fileContentUrl.startsWith('blob:')) {
            URL.revokeObjectURL(fileContentUrl);
        }
    };
  }, [isOpen, fileBlob, fileUrl, isExternalUrl]);


  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
        document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const mimeType = fileBlob?.type;

  const isImage = mimeType?.startsWith('image/') || /\.(jpe?g|png|gif|webp|svg)$/i.test(fileUrl || '');
  const isPdf = mimeType === 'application/pdf' || /\.pdf$/i.test(fileUrl || '');

  const renderContent = () => {
    if (isLoading) {
        return <Spinner text="Načítám soubor..." />;
    }
    if (error) {
        return <div className="text-center text-red-500 p-8">{error}</div>;
    }
    if (!fileContentUrl) {
        return <div className="text-center text-gray-500 p-8">Soubor není k dispozici.</div>;
    }
    
    if (isImage) {
        return <img src={fileContentUrl} alt={effectiveFileName} className="max-w-full max-h-full object-contain" />;
    }
    if (isPdf) {
        return <iframe src={fileContentUrl} title={effectiveFileName} className="w-full h-full border-none" />;
    }
    
    // Fallback pro ostatní typy souborů
    return (
        <div className="flex flex-col items-center justify-center h-full p-8 bg-gray-100 rounded-md">
            <p className="text-gray-700 mb-2">Náhled pro tento typ souboru není k dispozici.</p>
            <p className="font-semibold text-gray-800 mb-4 break-all">{effectiveFileName}</p>
        </div>
    );
  };


  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex justify-center items-center" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="file-preview-title"
    >
      <div 
        className="bg-white rounded-lg shadow-xl m-4 w-full max-w-4xl h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b flex-shrink-0">
          <h2 id="file-preview-title" className="text-lg font-bold text-gray-800 truncate" title={effectiveFileName}>
            {effectiveFileName}
          </h2>
          <div className="flex items-center gap-4">
              {fileContentUrl && (
                  <a
                      href={isExternalUrl ? fileUrl! : fileContentUrl}
                      download={!isExternalUrl ? effectiveFileName : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center gap-2"
                      title={isExternalUrl ? `Otevřít soubor na nové kartě` : `Stáhnout soubor`}
                  >
                      <DownloadIcon className="w-4 h-4" />
                      {/* Změna: Text tlačítka se přizpůsobuje typu souboru */}
                      <span>{isExternalUrl ? 'Otevřít' : 'Stáhnout'}</span>
                  </a>
              )}
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Zavřít"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
          </div>
        </div>
        <div className="overflow-auto flex-grow p-4 flex items-center justify-center">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
